import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, Truck } from "lucide-react";
import { api, eur, dt, errMsg, download } from "@/lib/api";
import { useAuth, isAgent, canWrite } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailButton } from "@/components/MailButton";

const STATO = { aperto: ["Da evadere", "bg-amber-50 text-amber-700"], evaso: ["Evaso", "bg-emerald-50 text-emerald-700"], in_evasione: ["In evasione", "bg-slate-100 text-slate-600"] };

export default function Ordini() {
  const { user } = useAuth();
  const agent = isAgent(user);
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [stato, setStato] = useState("aperto");
  const load = useCallback(() => api.get("/ordini", { params: { stato: stato === "all" ? undefined : stato } }).then((r) => setItems(r.data)), [stato]);
  useEffect(() => { load(); }, [load]);
  const toDdt = async (o) => {
    if (!window.confirm(`Creare il DDT dall'ordine ${o.numero}?`)) return;
    try { const { data } = await api.post(`/ordini/${o.id}/ddt`); toast.success(`DDT ${data.ddt_numero} creato`); nav(`/app/ddt/${data.ddt_id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title={agent ? "I miei ordini" : "Ordini clienti"} subtitle={agent ? "Inserisci gli ordini dei clienti: l'ufficio prepara il DDT" : "Ordini inseriti dagli agenti, da trasformare in DDT"}>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger data-testid="orders-filter-status" className="w-40 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="aperto">Da evadere</SelectItem><SelectItem value="evaso">Evasi</SelectItem><SelectItem value="all">Tutti</SelectItem></SelectContent>
        </Select>
        {canWrite(user) && <Button asChild className="bg-sky-600 hover:bg-sky-700" data-testid="new-order-button"><Link to="/app/ordini/nuovo"><Plus className="mr-2 h-4 w-4" />Nuovo ordine</Link></Button>}
      </PageHeader>
      {items === null ? null : items.length === 0 ? <Empty text="Nessun ordine" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader><TableRow><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead>{!agent && <TableHead>Agente</TableHead>}<TableHead>Stato</TableHead><TableHead className="text-right">Totale</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell><Link to={`/app/ordini/${o.id}`} className="font-mono text-sm font-semibold text-sky-700 hover:underline">{o.numero}</Link></TableCell>
                  <TableCell className="text-sm">{dt(o.data)}</TableCell>
                  <TableCell className="text-sm">{o.controparte?.denominazione || `${o.controparte?.nome || ""} ${o.controparte?.cognome || ""}`}</TableCell>
                  {!agent && <TableCell className="text-sm">{o.agente_nome}</TableCell>}
                  <TableCell>
                    <span className={`rounded px-2 py-0.5 text-xs ${STATO[o.stato]?.[1]}`}>{STATO[o.stato]?.[0]}</span>
                    {o.ddt_id && <Link to={`/app/ddt/${o.ddt_id}`} className="ml-2 text-xs text-sky-700 underline">DDT {o.ddt_numero}</Link>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(o.totals.totale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" title="PDF" data-testid={`order-pdf-${o.id}`} onClick={() => download(`/ordini/${o.id}/pdf`).catch((e) => toast.error(e.message))}><FileDown className="h-4 w-4" /></Button>
                    {canWrite(user) && <MailButton icon path={`/ordini/${o.id}/email`} email={o.controparte?.email} what="la copia dell'ordine" testid={`order-email-${o.id}`} />}
                    {!agent && canWrite(user) && o.stato === "aperto" && <Button size="sm" variant="outline" data-testid={`order-to-ddt-${o.id}`} onClick={() => toDdt(o)}><Truck className="mr-1 h-4 w-4" />Crea DDT</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
